import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { AppUpdateProvider } from "@/features/appUpdate/AppUpdateProvider";
import { ServerUpdateBanner } from "@/features/serverUpdate/ServerUpdateBanner";
import { Layout } from "@/components/Layout";
import { ThemeProvider } from "@/theme/ThemeProvider";
import { ToastViewport } from "@/components/ui/toast/ToastViewport";
import { RequireAdmin } from "@/components/RequireAdmin";
import { RequireAuth } from "@/components/RequireAuth";
import { IncomingCallPage } from "@/pages/anruf/IncomingCallPage";
import { RequireAcl } from "@/components/RequireAcl";
import { RequireBueroOrAdmin } from "@/components/RequireBueroOrAdmin";
import { RequireZeiterfassungAccess, RequireAdminZeiterfassungAccess } from "@/components/RequireZeiterfassungAccess";
import { RequireMitarbeiterAccess } from "@/components/RequireMitarbeiterAccess";
import { AdminIndexPage } from "@/pages/admin/Index";
import { ZeiterfassungIndexPage } from "@/pages/admin/zeiterfassung/Index";
import { ZeiterfassungSettingsPage } from "@/pages/admin/zeiterfassung/einstellungen/Index";
import { MitarbeiterIndexPage } from "@/pages/admin/mitarbeiter/Index";
import { MitarbeiterDetailPage } from "@/pages/admin/mitarbeiter/Detail";
import { RequirePersonalSelfService } from "@/components/RequirePersonalSelfService";
import { PersonalIndexPage } from "@/pages/personal/Index";
import { PersonalLayout } from "@/pages/personal/PersonalLayout";
import { MeinePersonalPage } from "@/pages/personal/MeinePersonalPage";
import { MeineEinstellungenPage } from "@/pages/personal/MeineEinstellungenPage";
import { PersonalkostenPage } from "@/pages/personal/personalkosten/PersonalkostenPage";
import { RequirePersonalkostenAccess } from "@/components/RequirePersonalkostenAccess";
import { NachunternehmerPage } from "@/pages/personal/nachunternehmer/NachunternehmerPage";
import { RequireNachunternehmerAccess } from "@/components/RequireNachunternehmerAccess";
import { AdminAreaPage } from "@/pages/admin/AdminArea";
import { FieldTemplatesPage } from "@/pages/admin/FieldTemplates";
import { SettingsLayout } from "@/pages/admin/einstellungen/SettingsLayout";
import { SettingsIndexPage } from "@/pages/admin/einstellungen/Index";
import { StammdatenPage } from "@/pages/admin/einstellungen/StammdatenPage";
import { HeroPage } from "@/pages/admin/einstellungen/HeroPage";
import { VorlagenPage } from "@/pages/admin/einstellungen/VorlagenPage";
import { DokumenteSettingsPage } from "@/pages/admin/einstellungen/DokumentePage";
import { StatusSichtbarkeitPage } from "@/pages/admin/einstellungen/StatusSichtbarkeitPage";
import { GdLayoutConfiguratorPage } from "@/pages/admin/einstellungen/GdLayoutConfiguratorPage";
import { KommunikationPage } from "@/pages/admin/einstellungen/KommunikationPage";
import { TagesgeschaeftSettingsPage } from "@/pages/admin/einstellungen/TagesgeschaeftPage";
import { DruckPage } from "@/pages/admin/einstellungen/DruckPage";
import { SitzungenPage } from "@/pages/admin/einstellungen/SitzungenPage";
import { BestellungenSettingsPage } from "@/pages/admin/einstellungen/BestellungenPage";
import { TagesgeschaeftIndexPage } from "@/pages/tagesgeschaeft/Index";
import { VeranstaltungenIndexPage } from "@/pages/veranstaltungen/Index";
import { VeranstaltungenLayout } from "@/pages/veranstaltungen/Layout";
import { MerosPage } from "@/pages/veranstaltungen/Meros";
import { VeranstaltungenUebersichtPage } from "@/pages/veranstaltungen/Uebersicht";
import { ProjekteIndexPage } from "@/pages/projekte/Index";
import { ProjectDetailPage } from "@/pages/projekte/Detail";
import { userCanOpenProjectPage } from "@/projectOverview/permissions";
import { MaterialIndexPage } from "@/pages/material/Index";
import { AngebotsanfragenIndexPage } from "@/pages/angebotsanfragen/Index";
import { BestellungenIndexPage } from "@/pages/bestellungen/Index";
import { BestellwesenLayout } from "@/pages/bestellwesen/BestellwesenLayout";
import { BestellwesenUebersichtPage } from "@/pages/bestellwesen/Uebersicht";
import { BestellwesenLegacyRedirect } from "@/pages/bestellwesen/LegacyRedirect";
import { DokumenteIndexPage } from "@/pages/dokumente/Index";
import { DokumenteLayout } from "@/pages/dokumente/DokumenteLayout";
import { DokumenteSectionHubPage } from "@/pages/dokumente/SectionHubPage";
import { BuchhaltungUebersichtPage } from "@/pages/dokumente/BuchhaltungUebersichtPage";
import { OffenePostenPage } from "@/pages/dokumente/OffenePostenPage";
import { MahnwesenPage } from "@/pages/dokumente/MahnwesenPage";
import { MahnungEditorPage } from "@/pages/dokumente/MahnungEditorPage";
import { DokumenteEinstellungenPage } from "@/pages/dokumente/EinstellungenPage";
import { ArtikelIndexPage } from "@/pages/artikel/Index";
import { ArtikelEinstellungenPage } from "@/pages/artikel/EinstellungenPage";
import { DokumentCreatePage } from "@/pages/dokumente/CreatePage";
import { DokumentEditorPage } from "@/pages/dokumente/EditorPage";
import { AnfragenIndexPage } from "@/pages/anfragen/Index";
import { InvoiceMailIndexPage } from "@/pages/invoiceMail/Index";
import { InvoiceApprovalIndexPage } from "@/pages/invoiceApproval/Index";
import { EingangsrechnungArtikelIndexPage } from "@/pages/eingangsrechnungArtikel/Index";
import { StundennachweiseIndexPage } from "@/pages/stundennachweise/Index";
import { LieferscheineIndexPage } from "@/pages/lieferscheine/Index";
import { BueroDokumenteRedirect } from "@/components/BueroDokumenteRedirect";
import { AuswertungenIndexPage } from "@/pages/auswertungen/Index";
import { AuswertungenLayout } from "@/pages/auswertungen/AuswertungenLayout";
import { HeroUsageReportPage } from "@/pages/auswertungen/HeroUsageReport";
import { HeroUsageRankingsPage } from "@/pages/auswertungen/HeroUsageRankingsPage";
import { ProjectCompletionsPage } from "@/pages/auswertungen/ProjectCompletionsPage";
import { FestpreisHoursPage } from "@/pages/auswertungen/FestpreisHoursPage";
import { HoursPipelinePage } from "@/pages/auswertungen/HoursPipelinePage";
import { OutstandingBalancesPage } from "@/pages/auswertungen/OutstandingBalancesPage";
import { PortalUsagePage } from "@/pages/auswertungen/PortalUsagePage";
import { InvoiceRevenuePage } from "@/pages/auswertungen/InvoiceRevenuePage";
import { OpenInvoicesPage } from "@/pages/auswertungen/OpenInvoicesPage";
import { OfferStatisticsPage } from "@/pages/auswertungen/OfferStatisticsPage";
import { DocumentMarginsPage } from "@/pages/auswertungen/DocumentMarginsPage";
import { SupportIndexPage } from "@/pages/support/Index";
import { SchwarzesBrettIndexPage } from "@/pages/schwarzesBrett/Index";
import { HomeIndexPage } from "@/pages/home/Index";
import { LoginPage } from "@/pages/auth/LoginPage";
import { NotFoundPage } from "@/pages/NotFound";
import { MontageplanungIndexPage } from "@/pages/montageplanung/Index";
import { MontageplanVollbildPage } from "@/pages/montageplan/VollbildPage";
import { WartungenIndexPage } from "@/pages/wartungen/Index";
import { WartungenEinstellungenPage } from "@/pages/wartungen/Einstellungen";
import { WartungenKategoriePage } from "@/pages/wartungen/Kategorie";
import { WartungenFormularePage } from "@/pages/wartungen/Formulare";
import { KategorieEinstellungenPage } from "@/pages/wartungen/KategorieEinstellungen";
import { VertraegePage } from "@/pages/wartungen/Vertraege";
import { VertragFormPage } from "@/pages/wartungen/VertragForm";
import { VertragsWartungenPage } from "@/pages/wartungen/VertragsWartungen";
import { ProjektStartPage } from "@/pages/wartungen/ProjektStart";
import { ObjektUebersichtPage } from "@/pages/wartungen/ObjektUebersicht";
import { ObjektAuswahlPage } from "@/pages/wartungen/ObjektAuswahl";
import { StandortObjektAuswahlPage } from "@/pages/wartungen/StandortObjektAuswahl";
import { ObjektAnlegenPage } from "@/pages/wartungen/ObjektAnlegen";
import { HuelleDetailPage } from "@/pages/wartungen/HuelleDetail";
import { WartungAuswertungPage } from "@/pages/wartungen/Auswertung";
import { FormPage } from "@/pages/wartungen/Form";
import { BrandschutztuerwartungPage } from "@/pages/wartungen/Brandschutztuerwartung";
import { OffpaperIndexPage } from "@/pages/offpaper/Index";
import { KontakteLayout } from "@/pages/kontakte/KontakteLayout";
import { KundenIndexPage } from "@/pages/kontakte/kunden/Index";
import { KundeDetailPage } from "@/pages/kontakte/kunden/Detail";
import { LieferantenIndexPage } from "@/pages/kontakte/lieferanten/Index";
import { LieferantDetailPage } from "@/pages/kontakte/lieferanten/Detail";

export function App() {
  return (
    <ThemeProvider>
      <ServerUpdateBanner />
      <ToastViewport />
      <BrowserRouter>
        <AppUpdateProvider>
        <Routes>
          <Route path="login" element={<LoginPage />} />
          <Route path="montageplan/vollbild" element={<MontageplanVollbildPage />} />
          <Route
            path="anruf"
            element={
              <RequireAuth>
                <IncomingCallPage />
              </RequireAuth>
            }
          />
          <Route element={<Layout />}>
            <Route
              index
              element={
                <RequireAuth>
                  <HomeIndexPage />
                </RequireAuth>
              }
            />

            <Route
              path="projekte"
              element={
                <RequireAuth>
                  <ProjekteIndexPage />
                </RequireAuth>
              }
            />

            <Route
              path="projekte/:orderId"
              element={
                <RequireAuth>
                  <RequireAdmin
                    allow={userCanOpenProjectPage}
                    deniedMessage="Die Projektseite ist für das Büro."
                  >
                    <ProjectDetailPage />
                  </RequireAdmin>
                </RequireAuth>
              }
            />

            <Route
              path="bestellwesen"
              element={
                <RequireAuth>
                  <BestellwesenLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="uebersicht" replace />} />
              <Route
                path="uebersicht"
                element={<BestellwesenUebersichtPage />}
              />
              <Route
                path="bestellungen"
                element={
                  <RequireAcl href="/bestellungen.html">
                    <BestellungenIndexPage />
                  </RequireAcl>
                }
              />
              <Route
                path="materialanforderungen"
                element={
                  <RequireAcl href="/materialanforderungen.html">
                    <MaterialIndexPage />
                  </RequireAcl>
                }
              />
              <Route
                path="anfragen"
                element={
                  <RequireAcl href="/anfragen.html">
                    <AnfragenIndexPage />
                  </RequireAcl>
                }
              />
              <Route
                path="artikel"
                element={
                  <RequireAcl href="/artikel.html">
                    <Outlet />
                  </RequireAcl>
                }
              >
                <Route index element={<ArtikelIndexPage />} />
                <Route path="einstellungen" element={<ArtikelEinstellungenPage />} />
              </Route>
              <Route
                path="rechnungs-postfach"
                element={
                  <RequireAcl href="/invoice-mail.html">
                    <InvoiceMailIndexPage />
                  </RequireAcl>
                }
              />
              <Route
                path="rechnungsfreigabe"
                element={
                  <RequireAcl href="/invoice-mail.html">
                    <InvoiceApprovalIndexPage />
                  </RequireAcl>
                }
              />
              <Route
                path="eingangsrechnung-artikel"
                element={
                  <RequireAcl href="/invoice-mail.html">
                    <EingangsrechnungArtikelIndexPage />
                  </RequireAcl>
                }
              />
            </Route>

            <Route
              path="materialanforderungen"
              element={
                <RequireAuth>
                  <BestellwesenLegacyRedirect hubSegment="materialanforderungen">
                    <RequireAcl href="/materialanforderungen.html">
                      <MaterialIndexPage />
                    </RequireAcl>
                  </BestellwesenLegacyRedirect>
                </RequireAuth>
              }
            />

            <Route
              path="angebotsanfragen"
              element={
                <RequireAuth>
                  <BueroDokumenteRedirect to="/dokumente/angebotsanfragen">
                    <RequireAcl href="/angebotsanfragen.html">
                      <AngebotsanfragenIndexPage />
                    </RequireAcl>
                  </BueroDokumenteRedirect>
                </RequireAuth>
              }
            />

            <Route
              path="bestellungen"
              element={
                <RequireAuth>
                  <BestellwesenLegacyRedirect hubSegment="bestellungen">
                    <RequireAcl href="/bestellungen.html">
                      <BestellungenIndexPage />
                    </RequireAcl>
                  </BestellwesenLegacyRedirect>
                </RequireAuth>
              }
            />

            <Route
              path="dokumente"
              element={
                <RequireAuth>
                  <RequireAcl href="/dokumente.html">
                    <DokumenteLayout />
                  </RequireAcl>
                </RequireAuth>
              }
            >
              <Route index element={<DokumenteIndexPage />} />
              <Route path="buchhaltung" element={<BuchhaltungUebersichtPage />} />
              <Route path="rechnungen" element={<DokumenteIndexPage lockedTyp="rechnung" />} />
              <Route path="offene-posten" element={<OffenePostenPage />} />
              <Route path="nachweise" element={<DokumenteSectionHubPage section="nachweise" />} />
              <Route path="angebotswesen" element={<DokumenteSectionHubPage section="angebotswesen" />} />
              <Route path="angebote" element={<DokumenteIndexPage lockedTyp="angebot" />} />
              <Route path="angebotsanfragen" element={<AngebotsanfragenIndexPage />} />
              <Route
                path="stundennachweise"
                element={<StundennachweiseIndexPage section="timesheets" />}
              />
              <Route
                path="fertigstellungsmeldungen"
                element={<StundennachweiseIndexPage section="project-completions" />}
              />
              <Route path="lieferscheine" element={<LieferscheineIndexPage />} />
              <Route path="mahnwesen" element={<MahnwesenPage />} />
              <Route path="mahnwesen/:id" element={<MahnungEditorPage />} />
              <Route path="layout" element={<GdLayoutConfiguratorPage />} />
              <Route path="einstellungen" element={<DokumenteEinstellungenPage />} />
              <Route path="neu" element={<DokumentCreatePage />} />
              <Route path=":id" element={<DokumentEditorPage />} />
            </Route>

            <Route
              path="kontakte"
              element={
                <RequireAuth>
                  <RequireAcl href="/kontakte.html">
                    <KontakteLayout />
                  </RequireAcl>
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="kunden" replace />} />
              <Route path="kunden" element={<KundenIndexPage />} />
              <Route path="kunden/:id" element={<KundeDetailPage />} />
              <Route path="lieferanten" element={<LieferantenIndexPage />} />
              <Route path="lieferanten/:id" element={<LieferantDetailPage />} />
            </Route>

            <Route
              path="artikel"
              element={
                <RequireAuth>
                  <BestellwesenLegacyRedirect hubSegment="artikel">
                    <RequireAcl href="/artikel.html">
                      <Outlet />
                    </RequireAcl>
                  </BestellwesenLegacyRedirect>
                </RequireAuth>
              }
            >
              <Route index element={<ArtikelIndexPage />} />
              <Route path="einstellungen" element={<ArtikelEinstellungenPage />} />
            </Route>

            <Route
              path="anfragen"
              element={
                <RequireAuth>
                  <BestellwesenLegacyRedirect hubSegment="anfragen">
                    <RequireAcl href="/anfragen.html">
                      <AnfragenIndexPage />
                    </RequireAcl>
                  </BestellwesenLegacyRedirect>
                </RequireAuth>
              }
            />

            <Route
              path="rechnungs-postfach"
              element={
                <RequireAuth>
                  <BestellwesenLegacyRedirect hubSegment="rechnungs-postfach">
                    <RequireAcl href="/invoice-mail.html">
                      <InvoiceMailIndexPage />
                    </RequireAcl>
                  </BestellwesenLegacyRedirect>
                </RequireAuth>
              }
            />

            <Route
              path="rechnungsfreigabe"
              element={
                <RequireAuth>
                  <BestellwesenLegacyRedirect hubSegment="rechnungsfreigabe">
                    <RequireAcl href="/invoice-mail.html">
                      <InvoiceApprovalIndexPage />
                    </RequireAcl>
                  </BestellwesenLegacyRedirect>
                </RequireAuth>
              }
            />

            <Route
              path="eingangsrechnung-artikel"
              element={
                <RequireAuth>
                  <BestellwesenLegacyRedirect hubSegment="eingangsrechnung-artikel">
                    <RequireAcl href="/invoice-mail.html">
                      <EingangsrechnungArtikelIndexPage />
                    </RequireAcl>
                  </BestellwesenLegacyRedirect>
                </RequireAuth>
              }
            />

            <Route
              path="stundennachweise"
              element={
                <RequireAuth>
                  <BueroDokumenteRedirect to="/dokumente/stundennachweise">
                    <StundennachweiseIndexPage />
                  </BueroDokumenteRedirect>
                </RequireAuth>
              }
            />

            <Route
              path="auswertungen"
              element={
                <RequireAuth>
                  <RequireAcl href="/auswertungen.html">
                    <AuswertungenLayout />
                  </RequireAcl>
                </RequireAuth>
              }
            >
              <Route index element={<AuswertungenIndexPage />} />
              <Route path="hero-zeiten" element={<HeroUsageReportPage />} />
              <Route path="offene-stunden" element={<HeroUsageRankingsPage />} />
              <Route path="stunden-pipeline" element={<HoursPipelinePage />} />
              <Route path="aussenstaende" element={<OutstandingBalancesPage />} />
              <Route path="projektabschluesse" element={<ProjectCompletionsPage />} />
              <Route path="festpreis-stunden" element={<FestpreisHoursPage />} />
              <Route
                path="umsatz"
                element={
                  <RequireAcl href="/auswertungen/umsatz.html">
                    <InvoiceRevenuePage />
                  </RequireAcl>
                }
              />
              <Route path="offene-posten" element={<OpenInvoicesPage />} />
              <Route path="angebote" element={<OfferStatisticsPage />} />
              <Route path="marge" element={<DocumentMarginsPage />} />
              <Route path="portal-nutzung" element={<PortalUsagePage />} />
            </Route>

            <Route
              path="support"
              element={
                <RequireAuth>
                  <SupportIndexPage />
                </RequireAuth>
              }
            />

            <Route
              path="schwarzes-brett"
              element={
                <RequireAuth>
                  <SchwarzesBrettIndexPage />
                </RequireAuth>
              }
            />

            <Route
              path="admin"
              element={
                <RequireAdmin>
                  <AdminIndexPage />
                </RequireAdmin>
              }
            />
            <Route
              path="admin/admin-area"
              element={
                <RequireAdmin>
                  <AdminAreaPage />
                </RequireAdmin>
              }
            />
            <Route
              path="admin/field-templates"
              element={
                <RequireAdmin>
                  <FieldTemplatesPage />
                </RequireAdmin>
              }
            />
            <Route
              element={
                <RequireAuth>
                  <PersonalLayout />
                </RequireAuth>
              }
            >
              <Route
                path="admin/mitarbeiter"
                element={
                  <RequireMitarbeiterAccess>
                    <MitarbeiterIndexPage />
                  </RequireMitarbeiterAccess>
                }
              />
              <Route
                path="admin/mitarbeiter/:login"
                element={
                  <RequireMitarbeiterAccess>
                    <MitarbeiterDetailPage />
                  </RequireMitarbeiterAccess>
                }
              />
              <Route path="personal" element={<PersonalIndexPage />} />
              <Route
                path="personal/me/einstellungen"
                element={
                  <RequirePersonalSelfService>
                    <MeineEinstellungenPage />
                  </RequirePersonalSelfService>
                }
              />
              <Route
                path="personal/me"
                element={
                  <RequirePersonalSelfService>
                    <MeinePersonalPage />
                  </RequirePersonalSelfService>
                }
              />
              <Route
                path="personal/personalkosten"
                element={
                  <RequirePersonalkostenAccess>
                    <PersonalkostenPage />
                  </RequirePersonalkostenAccess>
                }
              />
              <Route
                path="personal/nachunternehmer"
                element={
                  <RequireNachunternehmerAccess>
                    <NachunternehmerPage />
                  </RequireNachunternehmerAccess>
                }
              />
              <Route
                path="alt/nachunternehmer"
                element={<Navigate to="/personal/nachunternehmer" replace />}
              />
              <Route
                path="nachunternehmer"
                element={<Navigate to="/personal/nachunternehmer" replace />}
              />
              <Route
                path="zeiterfassung"
                element={
                  <RequireZeiterfassungAccess>
                    <ZeiterfassungIndexPage />
                  </RequireZeiterfassungAccess>
                }
              />
              <Route
                path="zeiterfassung/einstellungen"
                element={
                  <RequireZeiterfassungAccess>
                    <RequireBueroOrAdmin>
                      <ZeiterfassungSettingsPage />
                    </RequireBueroOrAdmin>
                  </RequireZeiterfassungAccess>
                }
              />
              <Route
                path="admin/zeiterfassung"
                element={
                  <RequireAdminZeiterfassungAccess>
                    <ZeiterfassungIndexPage />
                  </RequireAdminZeiterfassungAccess>
                }
              />
              <Route
                path="admin/zeiterfassung/einstellungen"
                element={
                  <RequireAdminZeiterfassungAccess>
                    <RequireBueroOrAdmin>
                      <ZeiterfassungSettingsPage />
                    </RequireBueroOrAdmin>
                  </RequireAdminZeiterfassungAccess>
                }
              />
            </Route>
            <Route
              path="admin/einstellungen"
              element={
                <RequireAdmin>
                  <SettingsLayout />
                </RequireAdmin>
              }
            >
              <Route index element={<SettingsIndexPage />} />
              <Route path="stammdaten" element={<StammdatenPage />} />
              <Route path="hero" element={<HeroPage />} />
              <Route path="vorlagen" element={<VorlagenPage />} />
              <Route path="dokumente" element={<DokumenteSettingsPage />} />
              <Route
                path="dokumente/layout"
                element={<Navigate to="/dokumente/layout" replace />}
              />
              <Route path="status-sichtbarkeit" element={<StatusSichtbarkeitPage />} />
              <Route path="bestellungen" element={<BestellungenSettingsPage />} />
              <Route path="kommunikation" element={<KommunikationPage />} />
              <Route path="tagesgeschaeft" element={<TagesgeschaeftSettingsPage />} />
              <Route path="druck" element={<DruckPage />} />
              <Route path="sitzungen" element={<SitzungenPage />} />
            </Route>

            <Route
              path="tagesgeschaeft"
              element={
                <RequireAuth>
                  <RequireAcl href="/tagesgeschaeft.html">
                    <TagesgeschaeftIndexPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />

            <Route
              path="veranstaltungen"
              element={
                <RequireAuth>
                  <VeranstaltungenLayout />
                </RequireAuth>
              }
            >
              <Route index element={<VeranstaltungenUebersichtPage />} />
              <Route path="messe" element={<VeranstaltungenIndexPage />} />
              <Route path="meros" element={<MerosPage />} />
            </Route>

            <Route
              path="montageplanung"
              element={
                <RequireAuth>
                  <MontageplanungIndexPage />
                </RequireAuth>
              }
            />

            <Route
              path="wartungen"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <WartungenIndexPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/brandschutztuerwartung"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <BrandschutztuerwartungPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="offpaper"
              element={
                <RequireAuth>
                  <RequireAcl href="/offpaper.html">
                    <OffpaperIndexPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/einstellungen"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <WartungenEinstellungenPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/kategorie"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <WartungenKategoriePage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/formulare"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <WartungenFormularePage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/kategorie-einstellungen"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <KategorieEinstellungenPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/vertraege"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <VertraegePage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/vertrag-form"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <VertragFormPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/vertrags-wartungen"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <VertragsWartungenPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/projekt-start"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <ProjektStartPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/objekt-uebersicht"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <ObjektUebersichtPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/objekt-auswahl"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <ObjektAuswahlPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/standort-objekt-auswahl"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <StandortObjektAuswahlPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/objekt-anlegen"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <ObjektAnlegenPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/huelle-detail"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <HuelleDetailPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/auswertung"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <WartungAuswertungPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />
            <Route
              path="wartungen/form"
              element={
                <RequireAuth>
                  <RequireAcl href="/wartungen/">
                    <FormPage />
                  </RequireAcl>
                </RequireAuth>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        </AppUpdateProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
